import React, { useMemo } from 'react';
import { Box, Checkbox, InputBase, makeStyles, ButtonBase } from '@material-ui/core';
import moment from 'moment';
import _ from 'lodash';
import { AutoCompleteSelect } from '../../../components';
import useTimeslotDialogStatus from '../hooks/useTimeslotDialogStatus';
import { ReactComponent as CancelledIcon } from '../../../static/images/Cancelled.svg';
import { i18n } from '../../../language';
import LongRemark from '../../../components/LongRemark';
import envelopeIcon from '../../../static/images/envelope.png';
import AdHocNotifacationListDialog from './AdHocNotificationList';

const useStyles = makeStyles((theme) => ({
  subTitle: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.25rem',
    color: '#2e4469',
    marginBottom: '1.25rem',
  },
  textTitle: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
    marginBottom: '0.625rem',
  },
  textContent: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#7d86a9',
    marginBottom: '1.875rem',
    display: 'flex',
    alignItems: 'center',
    gridGap: '0.5rem',
    wordBreak: 'break-word',
  },
  cardRoot: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    minHeight: '5.2rem',
    borderRadius: '15px',
    backgroundColor: '#f5faff',
    flexDirection: 'column',
    gridGap: '0.2rem',
    padding: '1rem 1.938rem',
  },
  cardTitle: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    color: '#7d86a9',
  },
  cardContent: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.25rem',
    color: '#538fff',
    display: 'flex',
    alignItems: 'center',
  },
  inputTitle: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    color: '#666666',
    marginBottom: '0.938rem',
    display: 'flex',
    alignItems: 'center',
  },
  inputContent: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
  },
  zoomBtn: {
    width: '14.438rem',
    height: '4.688rem',
    borderRadius: '9px',
    backgroundColor: '#4d51b5',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#ffffff',
    '&:disabled': {
      backgroundColor: '#c0c6cb !important',
      color: '#ffffff !important',
    },
  },
  airplaneIcon: {
    fontSize: '1.5rem',
    color: '#fff',
  },
  zoomTitle: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
    marginBottom: '0.625rem',
    wordBreak: 'break-all',
    width: '42rem',
  },
  zoomContent: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#7d86a9',
  },
  input: {
    width: '20.538rem',
    height: '3.5rem',
    borderRadius: '10px',
    border: 'solid 1px #e4e7eb',
    padding: '0 1.25rem',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
    '&.MuiInputBase-root.Mui-disabled': {
      backgroundColor: '#f5f5f5',
      color: '#2e4469',
    },
    '&.Mui-error': {
      borderColor: '#f44336',
    },
  },
  errorMsg: {
    color: 'rgb(244, 67, 54)',
    width: '100%',
    padding: '3px 0px 0px',
    fontFamily: 'NotoSans-Medium',
    fontWeight: '500',
  },
  star: {
    fontSize: '0.875rem',
    color: '#ff0000',
  },
  bigStart: {
    fontSize: '1.25rem',
    color: '#ff0000',
  },
  confirmContent: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
  },
  cancelInput: {
    borderRadius: '10px',
    border: 'solid 1px #e4e7eb',
    height: '3.5rem',
    width: '100%',
    paddingLeft: '1.25rem',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
  },
  circle: {
    width: '0.75rem',
    height: '0.75rem',
    borderRadius: '50%',
    backgroundColor: '#bad2ff',
  },
  dashedCircle: {
    width: '0.75rem',
    height: '0.75rem',
    borderRadius: '50%',
    border: 'dashed 1px #cdcdcd',
  },
  unavailableIcon: {
    width: '0.75rem',
    height: '0.75rem',
    borderRadius: '50%',
    backgroundColor: '#e0e0e0',
  },
  verifyingIcon: {
    width: '0.75rem',
    height: '0.75rem',
    borderRadius: '50%',
    backgroundColor: '#decfff',
  },
  cancelledIcon: {
    fontSize: '0.75rem',
  },
  checkBox: {
    '&.MuiCheckbox-root': {
      color: '#c0c6cb',
    },
    '&.MuiTypography-root': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '1rem',
      fontWeight: '500',
      color: '#2e4469',
    },
    '&.Mui-checked': {
      color: '#28d2ab',
    },
    justifyContent: 'flex-start',
  },
  label: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
  },
  textContentText: {
    flex: '1 0',
  },
  notificationBtn: {
    // marginLeft: '1rem',
    width: '8.438rem',
    height: '6.288rem',
    borderRadius: '9px',
    backgroundColor: '#4d51b5',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#ffffff',
    '&:disabled': {
      backgroundColor: '#c0c6cb !important',
      color: '#ffffff !important',
    },
  },
}));

const TimeslotDetail = ({ handleClose, info }) => {
  const classes = useStyles();

  const {
    moduleDisplay,
    data,
    handleOpen,
    updateData,
    itemLists,
    openCtrl,
    infoErrorMsg,
    handleLang,
    currentlyEffective,
  } = useTimeslotDialogStatus({
    info,
    handleClose,
  });

  const disabled = true;
  const StatusIcon = useMemo(() => {
    switch (data.status) {
      case i18n.t('TELEVISIT_SCHEDULED'):
      case i18n.t('TELEVISIT_CONFIRMED_SCHEDULED'):
      case i18n.t('TELEVISIT_ROBOT_STARTS_PROCESS_THE_BOOKING'):
      case i18n.t('TELEVISIT_VISIT_IN_PROGRESS'):
      case i18n.t('TELEVISIT_VISIT_COMPLETED'):
        return <div className={classes.circle} />;
      case i18n.t('TELEVISIT_AVAILABLE'):
        return <div className={classes.dashedCircle} />;
      case i18n.t('TELEVISIT_UNAVAILABLE'):
        return <div className={classes.unavailableIcon} />;
      case i18n.t('TELEVISIT_VERIFYING'):
        return <div className={classes.verifyingIcon} />;
      case i18n.t('TELEVISIT_CANCELLED'):
        return <CancelledIcon className={classes.cancelledIcon} />;
      default:
        return <div />;
    }
  }, [data.status]);

  const longRemarkContent = useMemo(() => {
    let remark;
    if (!data) return '';
    if (data.cancelReasonType === '9') {
      remark = data?.otherCancelReasonMsg;
    } else {
      remark = _.find(itemLists?.cancelTypeRemark, (item) => item.value === data.cancelReasonType)?.text;
    }
    return remark;
  }, [itemLists?.cancelTypeRemark, data.cancelReasonType]);

  const longRemarkContentForRejected = useMemo(() => {
    let remark;
    switch (data.rejectReasonType) {
      case 1:
        return i18n.t('TELEVISIT_VISIT_REJECTED_REASON1');
      case 2:
        return i18n.t('TELEVISIT_VISIT_REJECTED_REASON2');
      case 3:
        return i18n.t('TELEVISIT_VISIT_REJECTED_REASON3');
      case 4:
        return i18n.t('TELEVISIT_VISIT_REJECTED_REASON4');
      default:
        return remark;
    }
  }, [data.rejectReasonType]);

  return (
    <>
      {openCtrl.timeslot && info.bookId && (
        <Box>
          <Box display="flex" borderLeft="solid 1px #e4e7eb">
            <Box display="flex" flexDirection="column" pr="1rem" pl="1rem" width="35%" borderRight="solid 1px #e4e7eb">
              {moduleDisplay.basicInfo && (
                <>
                  <div className={classes.subTitle}> {i18n.t('TELEVISIT_POPUP_BASIC_INFORMATION')}</div>
                  <Box display="flex">
                    <Box display="flex" flexDirection="column" flexBasis="50%">
                      <div className={classes.textTitle}>{i18n.t('TELEVISIT_POPUP_BOOKING_ID')}</div>
                      <div className={classes.textContent}>{data.bookingId}</div>
                    </Box>
                    <Box display="flex" flexDirection="column" flexBasis="50%">
                      <div className={classes.textTitle}>{i18n.t('TELEVISIT_POPUP_APPLICATION_TIME')}</div>
                      <div className={classes.textContent}>
                        {data.applicationTime ? moment(data.applicationTime, 'x').format('YYYY-MM-DD HH:mm') : ''}
                      </div>
                    </Box>
                  </Box>
                </>
              )}
              <Box display="flex">
                <Box display="flex" flexDirection="column" flexBasis="50%">
                  <div className={classes.textTitle}>{i18n.t('TELEVISIT_POPUP_ROBOT_IN_USE')}</div>
                  <div className={classes.textContent}>{data.robotInUse}</div>
                </Box>
                <Box display="flex" flexDirection="column" flexBasis="50%">
                  <div className={classes.textTitle}>{i18n.t('TELEVISIT_POPUP_STATUS')}</div>
                  <div className={classes.textContent}>
                    {StatusIcon}
                    <div className={classes.textContentText} style={{ display: 'flex', alignItems: 'center' }}>
                      <div dangerouslySetInnerHTML={{ __html: typeof data.status === 'string' ? data.status : '' }} />
                      {data.timeslotBookingStatusEnum === 7 && (
                        <LongRemark content={longRemarkContent} width="100%" minWidth="10rem" />
                      )}
                      {data.timeslotBookingStatusEnum === 6 && (
                        <LongRemark content={longRemarkContentForRejected} width="100%" minWidth="10rem" />
                      )}
                    </div>
                  </div>
                </Box>
              </Box>
              <div className={classes.subTitle}>{i18n.t('TELEVISIT_POPUP_PATIENT_INFORMATION')}</div>
              <Box display="flex" flexDirection="column" gridGap="1.25rem">
                <div className={classes.cardRoot}>
                  <div className={classes.cardTitle}>{i18n.t('TELEVISIT_POPUP_LOCATION')}</div>
                  <div className={classes.cardContent}>{data.location}</div>
                </div>
                <div className={classes.cardRoot}>
                  <div className={classes.cardTitle}>{i18n.t('TELEVISIT_POPUP_PATIENT_NAME')}</div>
                  <div className={classes.cardContent}>{data.patientName}</div>
                </div>
                <div className={classes.cardRoot}>
                  <div className={classes.cardTitle}>{i18n.t('TELEVISIT_POPUP_PATIENT_ID')}</div>
                  <div className={classes.cardContent}>{data.patientId}</div>
                </div>
              </Box>
            </Box>
            <Box display="flex" flexDirection="column" pl="1rem" width="65%">
              <div className={classes.subTitle}>{i18n.t('TELEVISIT_POPUP_SELECTED_TIMESLOT')}</div>
              {moduleDisplay.baseDateTime && (
                <Box display="flex" justifyContent="space-between" gridGap="1.438rem" mb="1.875rem">
                  <Box display="flex" flexDirection="column">
                    <div className={classes.inputTitle}>{i18n.t('TELEVISIT_POPUP_DATE')}</div>
                    <VisitorInput
                      className={classes.input}
                      value={moment(data.date, 'x').format('yyyy-MM-DD')}
                      {...{ disabled: true }}
                    />
                  </Box>
                  <Box display="flex" flexDirection="column">
                    <div className={classes.inputTitle}>{i18n.t('TELEVISIT_POPUP_TIME')}</div>
                    <VisitorInput className={classes.input} value={data.time} {...{ disabled: true }} />
                  </Box>
                </Box>
              )}

              {moduleDisplay.urgentTimeBook && (
                <>
                  {moduleDisplay.durationAndBufferTime && (
                    <Box display="flex" justifyContent="space-between" gridGap="1.438rem" mb="1.875rem">
                      <Box display="flex" flexDirection="column">
                        <VisitorFieldWrap className={classes.inputTitle} title={i18n.t('TELEVISIT_POPUP_DURATION')} />

                        <VisitorInput className={classes.input} value={data.durationTime} {...{ disabled: true }} />
                      </Box>

                      <Box display="flex" flexDirection="column">
                        <VisitorFieldWrap
                          className={classes.inputTitle}
                          title={i18n.t('TELEVISIT_SETTING_POPUP_BUFFER_TIME')}
                          remark={i18n.t('TELEVISIT_SETTING_POPUP_BUFFER_TIME_REMARK')}
                        />

                        <VisitorInput className={classes.input} value={data.bufferTime} {...{ disabled: true }} />
                      </Box>
                    </Box>
                  )}
                  <Box display="flex" justifyContent="space-between" gridGap="1.438rem" mb="1.875rem">
                    <Box display="flex" flexDirection="column">
                      <div className={classes.inputTitle}>{i18n.t('TELEVISIT_POPUP_STARTDATE')}</div>
                      <VisitorInput
                        className={classes.input}
                        value={moment(data.date, 'x').format('yyyy-MM-DD')}
                        {...{ disabled: true }}
                      />
                    </Box>
                    <Box display="flex" flexDirection="column">
                      <div className={classes.inputTitle}>{i18n.t('TELEVISIT_POPUP_STARTTIME')}</div>
                      <VisitorInput className={classes.input} value={data.startTime} {...{ disabled: true }} />
                    </Box>
                  </Box>
                  <Box display="flex" justifyContent="space-between" gridGap="1.438rem" mb="1.875rem">
                    <Box display="flex" flexDirection="column">
                      <div className={classes.inputTitle}>{i18n.t('TELEVISIT_POPUP_ENDDATE')}</div>
                      <VisitorInput
                        className={classes.input}
                        value={moment(data.endDate, 'x').format('yyyy-MM-DD')}
                        {...{ disabled: true }}
                      />
                    </Box>
                    <Box display="flex" flexDirection="column">
                      <div className={classes.inputTitle}>{i18n.t('TELEVISIT_POPUP_ENDTIME')}</div>
                      <VisitorInput className={classes.input} value={data.endTime} {...{ disabled: true }} />
                    </Box>
                  </Box>
                </>
              )}

              {moduleDisplay.televisitLink && (
                <>
                  <div className={classes.subTitle} style={{ margin: '0' }}>
                    {i18n.t('TELEVISIT_POPUP_TELEVISIT_INFORMATION')}
                  </div>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb="1.875rem" mt="1.25rem">
                    <Box display="flex" flexDirection="column">
                      <div className={classes.zoomTitle}> {i18n.t('TELEVISIT_POPUP_ZOOM_LINK')}</div>
                      <div className={classes.zoomTitle}> {data.zoomLink}</div>
                      <Box display="flex" gridGap="2rem">
                        <div className={classes.zoomContent}>{`${i18n.t('TELEVISIT_POPUP_MEETING_ID')} ${
                          data.meetingId
                        }`}</div>
                        <div className={classes.zoomContent}>{`${i18n.t('TELEVISIT_POPUP_PASSWORD')} ${
                          data.meetingPw
                        }`}</div>
                      </Box>
                    </Box>
                  </Box>
                </>
              )}
              <div className={classes.subTitle}>{i18n.t('TELEVISIT_POPUP_VISITOR_INFORMATION')}</div>
              <Box display="flex" justifyContent="space-between" gridGap="1.438rem" mb="1.375rem">
                <Box display="flex" flexDirection="column">
                  <VisitorFieldWrap title={i18n.t('TELEVISIT_POPUP_VISITOR_FULL_NAME')} {...{ disabled: true }}>
                    <VisitorInput
                      className={classes.input}
                      value={data.visitorFullName}
                      {...{ disabled: true }}
                      onChange={updateData('visitorFullName', 'input')}
                      errorMsg={infoErrorMsg.visitorFullName}
                      placeholder={i18n.t('TELEVISIT_POPUP_VISITOR_FULL_NAME')}
                    />
                  </VisitorFieldWrap>
                </Box>
                <Box display="flex" flexDirection="column">
                  <VisitorFieldWrap title={i18n.t('TELEVISIT_POPUP_RELATIONSHIP')} {...{ disabled: true }}>
                    <AutoCompleteSelect
                      width="20.538rem"
                      value={data.relationship}
                      itemList={itemLists.relationshipList}
                      {...{ disabled: true }}
                      onChange={updateData('relationship', 'autocomplete')}
                      errorMsg={infoErrorMsg.relationship}
                      error={Boolean(infoErrorMsg.relationship)}
                      placeholder={i18n.t('TELEVISIT_POPUP_RELATIONSHIP')}
                    />
                  </VisitorFieldWrap>
                </Box>
              </Box>
              <Box display="flex" justifyContent="space-between" gridGap="1.438rem" mb="1.375rem">
                <Box display="flex" flexDirection="column">
                  <VisitorFieldWrap
                    title={i18n.t('TELEVISIT_POPUP_PHONE_NO')}
                    remark={i18n.t('TELEVISIT_POPUP_PHONE_NO_REMARK')}
                    {...{ disabled: true }}
                  >
                    <VisitorInput
                      className={classes.input}
                      value={data.contactPhoneNo}
                      {...{ disabled: true }}
                      onChange={updateData('contactPhoneNo', 'input')}
                      errorMsg={infoErrorMsg.contactPhoneNo}
                      placeholder={i18n.t('TELEVISIT_POPUP_PHONE_NO')}
                    />
                  </VisitorFieldWrap>
                </Box>
                {itemLists.smsTypeList.length > 0 && (
                  <Box display="flex" flexDirection="column" width="100%">
                    <VisitorFieldWrap
                      title={i18n.t('TELEVISIT_NOTIFICATION_LANGUAGE')}
                      {...{ disabled: true }}
                      required={false}
                      style={{ marginBottom: '0' }}
                    >
                      <Box display="flex" alignItems="center">
                        <Box alignItems="center">
                          <Box width="14rem" alignItems="center" ml="-10px">
                            <Box display="flex" alignItems="center">
                              <Checkbox
                                checked={data.langCode === itemLists.smsTypeList[1]?.value}
                                onChange={handleLang('CN')}
                                disabled={disabled}
                                pl="0"
                              />
                              <label className={classes.label}>{itemLists.smsTypeList[1]?.text ?? ''}</label>
                            </Box>
                            <Box pl="37px" mt="-8px">
                              {currentlyEffective === itemLists.smsTypeList[1]?.value && (
                                <label className={classes.label}>({i18n.t('TELEVISIT_CURRENTLY_EFFECTIVE')})</label>
                              )}
                            </Box>
                          </Box>
                          <Box width="14rem" alignItems="center" ml="-10px">
                            <Checkbox
                              checked={data.langCode === itemLists.smsTypeList[0]?.value}
                              onChange={handleLang('EN')}
                              disabled={disabled}
                            />
                            <label className={classes.label}>{itemLists.smsTypeList[0]?.text ?? ''}</label>
                            <Box pl="37px" mt="-8px">
                              {currentlyEffective === itemLists.smsTypeList[0]?.value && (
                                <label className={classes.label}>({i18n.t('TELEVISIT_CURRENTLY_EFFECTIVE')})</label>
                              )}
                            </Box>
                          </Box>
                        </Box>
                        <Box display="flex" justifyContent="flex-end">
                          <ButtonBase className={classes.notificationBtn} onClick={handleOpen('notificationList')}>
                            <img
                              src={envelopeIcon}
                              alt="Notification History"
                              style={{ width: 22, height: 20, color: '#ffffff', marginBottom: 2.2 }}
                            />
                            <div dangerouslySetInnerHTML={{ __html: i18n.t('TELEVISIT_POPUP_NOTIFICATION_LIST') }} />
                          </ButtonBase>
                        </Box>
                      </Box>
                    </VisitorFieldWrap>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      {openCtrl.notificationList && (
        <AdHocNotifacationListDialog open={true} handleClose={handleOpen('timeslot')} info={info} />
      )}
    </>
  );
};

function VisitorFieldWrap(props) {
  const { title, children, required, remark, style } = props;
  const classes = useStyles();
  return (
    <>
      <div className={classes.inputTitle} style={style}>
        {title}
        {required && <span className={classes.star}>*</span>}
        {remark && <LongRemark content={remark} width="100%" minWidth="10rem" />}
      </div>
      {children}
    </>
  );
}

const VisitorInput = ({ errorMsg, ...props }) => {
  const classes = useStyles();
  return (
    <>
      <InputBase {...props} error={Boolean(errorMsg)} />
      {errorMsg && <div className={classes.errorMsg}>{errorMsg}</div>}
    </>
  );
};

export default TimeslotDetail;
